
import React from "react";
import { i18nManager } from "../../../../../i18n";
import { NavLink, useSearchParams } from "react-router-dom";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button, fileInputFileFromUrl } from "@rivtn/noseur";
import { ApiResponseData, BaseService, SupportCategoryService, ICountry, ICurrency, ISupportCategory, IPlatform, SanitizedResponse, ConsusService } from "../../../../../services";

export interface CategoriesProps {
    platform: IPlatform;
}

function Categories(props: CategoriesProps) {
    const consusService = ConsusService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const supportCategoryService = SupportCategoryService.getInstance();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.platform_management.management.support.categories;
    const [supportCategories, setSupportCategories] = React.useState<ApiResponseData<ISupportCategory>>({});
    labels.sided_labels.dropdown_form![0].control.options = [{ label: i18nManager.Labels.common.all, value: "" }, ...i18nManager.Languages];

    return SidedContainer({
        searchParams,
        setSearchParams,
        data: supportCategories,
        onAction: manageCategory,
        queryData: fetchCategories,
        labels: labels.sided_labels,
        firstStyle: {
            marginTop: 15,
            padding: "0px 20px 20px",
            border: "1px solid #F0F0F0",
        },
        secondStyle: {
            padding: 15,
            marginTop: 15,
            border: "1px solid #F0F0F0",
        },
        dataTemplate: (category: ISupportCategory) => {
            return (<div className="hera-item" onClick={() => {
                manageCategory(category);
                Utils.updateSearchParams("adi", category.external_id, setSearchParams);
            }}>
                <img alt={category.name} src={category.icon_location} />
                <div>
                    <div className="title lined-limit">{category.name} <span> - {i18nManager.Languages.find((l) => l.value == category.language)?.label} ({category.language})</span></div>
                    <div className="content lined-limit">{category.description}</div>
                </div>
            </div>);
        },
    } as any);

    function fetchCategories() {
        supportCategoryService.queryCategories(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ISupportCategory>) => {
            setSupportCategories(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageCategory(category: ISupportCategory = {} as any) {
        const isUpdate = !!category.name;
        let selectedFile: File | undefined;
        const dialogLabels = labels.dialogs.add_edit;
        category.language = category.language ?? searchParams.get("language");

        const onFormInput = (name: string, value: any, search?: boolean) => {
            dialogErrorRef.current!.innerText = " ";
            (category as any)[name] = value;
            return false;
        }
        const dialog = ConfirmDialog({
            closable: true,
            title: (isUpdate ? dialogLabels.edit_title : dialogLabels.add_title),
            confirmLabel: (isUpdate ? dialogLabels.update_action_label : dialogLabels.add_action_label),
            style: { minWidth: 500, maxWidth: "80%" },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm([dialogLabels.form[0]], onFormInput, category, undefined, { marginTop: 0 }, {
                    "language": i18nManager.Languages
                })}
                <div style={{ display: "flex", marginTop: 15 }}>
                    <FileInput className="pic" stickyPreview rounded scheme={Schemes.RIVTN_QUIRINUS} accepts="image/*"
                        defaultFiles={category.icon_location ? [fileInputFileFromUrl(category.icon_location, "sname.png", `image/*`)] : undefined}
                        style={{ height: 60, width: 60, marginBottom: 0 }} control={<i className='fa fa-pen' style={{ cursor: "pointer" }} />}
                        attrsRelay={{ control: { style: { borderRadius: "50%" }, alignment: Alignment.BOTTOM_RIGHT } }} onSelectFiles={(files) => {
                            selectedFile = files[0];
                            if (!isUpdate) category.icon_location = "https://temp.png";
                        }} onRemoveFile={() => {
                            selectedFile = undefined;
                        }} />
                    {Elements.buildConfirmDialogForm([dialogLabels.form[1]], onFormInput, category, undefined, { marginTop: 0, flex: 1, marginLeft: 20 }, undefined, { marginTop: 5 })}
                </div>
                {Elements.buildConfirmDialogForm([dialogLabels.form[2]], onFormInput, category, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            footerElements: (isUpdate ? (<Button scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 30px" }} onClick={() => {
                dialog.hide(() => deleteCategory(category));
            }} />) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(category).length < 4) {
                    dialogErrorRef.current!.innerText = dialogLabels.fill_required_fields;
                    return false;
                }
                button?.setLoadingState(true);
                category.platform_id = props.platform.platform_id;
                const method = (isUpdate ? supportCategoryService.updateSupportCategory : supportCategoryService.createSupportCategory).bind(supportCategoryService);
                method(category).then(({ apiResponse: { data: category } }: SanitizedResponse<ISupportCategory>) => {
                    const done = () => {
                        Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.add_successful);
                        dialog.hide(fetchCategories);
                    };
                    consusService.uploadBinary({ file: selectedFile, fullname: `/support/cat/${category!.external_id}` }).then(async ({ apiResponse: { data } }: SanitizedResponse<any>) => {
                        console.log("THE. DATA", data.consus_download_url);
                        if (!isUpdate && category) {
                            category.platform_id = props.platform.platform_id;
                            category.icon_location = data.consus_download_url;
                            await supportCategoryService.updateSupportCategory(category as ISupportCategory);
                        }
                    }).catch(BaseService.reportError).finally(done);
                }).catch(({ errors }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function deleteCategory(category: ISupportCategory) {
        const dialog = ConfirmDialog({
            confirmScheme: Scheme.DANGER,
            title: labels.dialogs.delete.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            desc: labels.dialogs.delete.desc.replace("${name}", category.name)
                .replace("${language}", category.language),
            onCancel: () => {
                dialog.hide(() => manageCategory(category));
            },
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                consusService.deleteFiles([category.icon_location.substring(category.icon_location.indexOf("/storage/o/") + 11)]).catch(BaseService.reportError);
                supportCategoryService.deleteSupportCategory(category.external_id).then(({ }: SanitizedResponse<any>) => {
                    Siren.alert(labels.dialogs.delete.success);
                    dialog.hide(fetchCategories);
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Categories;
